/**
 * Helper: Algolia Client
 * -----------------------------------------------------------------------------
 * Shared singleton Algolia client for all components.
 * - Initializes once and reuses connection across components.
 * - Includes distinct wrapper for deduplication.
 * - Request batching for improved performance.
 *
 */
// eslint-disable-next-line import/named
import { markRaw } from 'vue'
import algoliasearch from 'algoliasearch/lite'

let sharedClient = null
let distinctSearchClient = null
let rawSearchClient = null

/**
 * Empty response for when Algolia is not configured.
 */
const emptyResponse = (requests) => Promise.resolve({
  results: (requests || []).map(() => ({
    hits: [],
    nbHits: 0,
    nbPages: 0,
    page: 0,
    processingTimeMS: 0,
    hitsPerPage: 0,
    exhaustiveNbHits: false,
    query: '',
    params: '',
  })),
})

/**
 * Get or create the shared Algolia client.
 * @returns {Object|null} Algolia client or null if not configured.
 */
export function getAlgoliaClient() {
  if (sharedClient !== null) {
    return sharedClient
  }

  const algoliaConfig = window.cnvs?.algolia || {}
  const credentials = algoliaConfig.credentials || {}

  if (!credentials.appId || !credentials.apiKey) {
    sharedClient = false
    return null
  }

  sharedClient = algoliasearch(credentials.appId, credentials.apiKey)
  return sharedClient
}

/**
 * Get a search client wrapper with distinct enabled for deduplication.
 * - Algolia Shopify integration indexes one record per variant,
 *   not per product.
 * - `distinct: true` deduplicates server-side using
 *   `attributeForDistinct`.
 * - Batches multiple search calls within the same into a single API request.
 * @returns {Object} Search client suitable for ais-instant-search.
 */
export function getDistinctSearchClient() {
  if (distinctSearchClient !== null) {
    return distinctSearchClient
  }

  const client = getAlgoliaClient()

  if (!client) {
    distinctSearchClient = markRaw({
      search: emptyResponse,
      searchForFacetValues: emptyResponse,
    })
    return distinctSearchClient
  }

  let pendingRequests = null
  let batchTimer = null

  distinctSearchClient = markRaw({
    search: (requests) => {
      const modifiedRequests = requests.map((request) => ({
        ...request,
        params: {
          ...request.params,
          distinct: true,
        },
      }))

      return new Promise((resolve, reject) => {
        if (!pendingRequests) {
          pendingRequests = []
        }

        pendingRequests.push({ requests: modifiedRequests, resolve, reject })

        if (batchTimer) {
          clearTimeout(batchTimer)
        }

        batchTimer = setTimeout(() => {
          const batch = pendingRequests
          pendingRequests = null
          batchTimer = null

          const allRequests = batch.flatMap((b) => b.requests)

          client.search(allRequests).then((response) => {
            let offset = 0
            batch.forEach((b) => {
              const count = b.requests.length
              b.resolve({
                results: response.results.slice(offset, offset + count),
              })
              offset += count
            })
          }).catch((error) => {
            batch.forEach((b) => b.reject(error))
          })
        }, 0)
      })
    },
    searchForFacetValues: client.searchForFacetValues
      ? (...args) => client.searchForFacetValues(...args)
      : emptyResponse,
  })

  return distinctSearchClient
}

/**
 * Get the raw Algolia client (without distinct wrapper).
 * - Useful for predictive search and other multi-index searches.
 * @returns {Object|null} Raw Algolia client wrapped for Vue reactivity.
 */
export function getRawSearchClient() {
  if (rawSearchClient !== null) {
    return rawSearchClient
  }

  const client = getAlgoliaClient()

  if (!client) {
    rawSearchClient = null
    return null
  }

  rawSearchClient = markRaw(client)
  return rawSearchClient
}

/**
 * Get Algolia configuration.
 * @returns {Object} Algolia config with indices and sort orders.
 */
export function getAlgoliaConfig() {
  const algoliaConfig = window.cnvs?.algolia || {}
  const baseIndex = algoliaConfig.indices?.products || 'shopify_products'
  const mainIndex = resolveMarketProductsIndex(baseIndex)

  return {
    mainIndex,
    sortOrders: normalizeSortOrderIndices(algoliaConfig.sortOrders || [], baseIndex, mainIndex),
    indices: algoliaConfig.indices || {},
  }
}

/**
 * Resolve the market-aware products index from the base products index.
 * - Expected pattern from Algolia Shopify Markets indexing:
 *   `<baseIndex>_<country_name_snake_case>_<language_code>`
 * - Example: `shopify_products_united_states_en`
 * @param {String} baseIndex - Base products index name.
 * @returns {String}
 */
function resolveMarketProductsIndex(baseIndex) {
  const store = window.cnvs?.store || {}
  const marketHandleSlug = toMarketSlug(store.marketHandle)
  const marketNameSlug = toMarketSlug(store.marketName)
  const isPrimaryMarket = parseMarketPrimaryFlag(store)
  const languageCode = normalizeLanguageCode(store.language)
  const countryNameSlug = toMarketSlug(store.countryName)
  const currencyCode = String(store.currency || '').trim().toUpperCase()
  const baseCurrencyCode = String(store.baseCurrency || '').trim().toUpperCase()

  // Algolia market suffixes align with market handle in Shopify Markets setup.
  // Market name is used only as a final fallback when handle is unavailable.
  const marketSuffix = marketHandleSlug || marketNameSlug

  // Safety guard for primary/base market when Shopify primary flag is not
  // reliably exposed to storefront JS.
  const isLikelyBaseMarket =
    Boolean(countryNameSlug) &&
    marketSuffix === countryNameSlug &&
    currencyCode &&
    baseCurrencyCode &&
    currencyCode === baseCurrencyCode

  // Primary market keeps the base index in Algolia Markets indexing.
  if (isPrimaryMarket || isLikelyBaseMarket || !marketSuffix || !languageCode) {
    return baseIndex
  }

  return `${baseIndex}_${marketSuffix}_${languageCode}`
}

/**
 * Normalize storefront language to Algolia market suffix format.
 * - Converts values like en-GB / en_GB to en.
 * @param {*} language - Storefront language value.
 * @returns {String}
 */
function normalizeLanguageCode(language) {
  const raw = String(language || '').trim()

  if (!raw) {
    return ''
  }

  const [baseLanguage] = raw.split(/[-_]/)
  return toMarketSlug(baseLanguage)
}

/**
 * Parse primary-market flag from store data.
 * @param {Object} store - Store object from theme variables.
 * @returns {Boolean}
 */
function parseMarketPrimaryFlag(store) {
  const value = store?.isPrimaryMarket

  return value === true || value === 'true' || value === 1 || value === '1'
}

/**
 * Slugify market labels for Algolia index naming.
 * @param {*} value - Raw value.
 * @returns {String}
 */
function toMarketSlug(value) {
  if (!value) {
    return ''
  }

  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Normalize sort order index names to the active market index.
 * - Handles values stored as:
 *   - `products_virtual_price_asc`
 *   - `shopify_products_virtual_price_asc`
 *   - already market-scoped index values
 * @param {Array} sortOrders - Raw sort orders.
 * @param {String} baseIndex - Base products index name.
 * @param {String} mainIndex - Market-aware products index name.
 * @returns {Array}
 */
function normalizeSortOrderIndices(sortOrders, baseIndex, mainIndex) {
  return sortOrders.map((sortOrder) => {
    const value = String(sortOrder?.value || '')

    if (!value) {
      return sortOrder
    }

    // Already market-aware.
    if (value.startsWith(`${mainIndex}_`)) {
      return sortOrder
    }

    // Full base index + replica suffix.
    if (value.startsWith(`${baseIndex}_`)) {
      const suffix = value.slice(baseIndex.length)
      return {
        ...sortOrder,
        value: `${mainIndex}${suffix}`,
      }
    }

    // Replica suffix configured without prefix in settings.
    if (value.startsWith('products_')) {
      const suffix = value.slice('products'.length)
      return {
        ...sortOrder,
        value: `${mainIndex}${suffix}`,
      }
    }

    return sortOrder
  })
}

/**
 * Get active filters from theme settings.
 * - Reads filters configured in Shopify theme settings (Algolia Filter Options)
 * - Filters can reference standard Algolia attributes or custom metafields
 * - Example: { label: 'Dresser', attribute: 'meta.custom.dresser' }
 * - Returns empty array if no filters configured
 * @returns {Array} Array of filter objects with { label, attribute }
 */
export function getActiveFilters() {
  const filterOptions = window.cnvs?.algolia?.filterOptions
  return Array.isArray(filterOptions) ? filterOptions : []
}
